import {useQuery} from "@tanstack/react-query";
import {getLocalStorageItem} from "../utils/localStorage";
import {LocalStorageError, LocalStorageErrorType} from "@/utils/localStorage";

export interface LocalStorageQueryOptions {
  staleTime?: number;
  gcTime?: number;
  ignoreParsErrors?: boolean;
}

/**
 * Query hook to fetch data from localStorage.
 */
export const useLocalStorageQuery = <T = unknown>(
  key: string,
  options: LocalStorageQueryOptions = {},
) => {
  const {
    // Time after which the data is considered stale (stale data will be refetched),
    // default value is 5 minutes.
    gcTime = 1000 * 60 * 10, // 10 minutes
    ignoreParsErrors = false,
  } = options;

  return useQuery({
    queryKey: ["localStorage", key],
    queryFn: async () => {
      try {
        return getLocalStorageItem(key) as T;
      } catch (error) {
        if (error instanceof LocalStorageError) {
          // Handle parse errors if ignoreParsErrors is true
          if (
            error.type === LocalStorageErrorType.PARSE_ERROR &&
            ignoreParsErrors
          ) {
            // Return raw string value instead of parsed JSON
            return localStorage.getItem(key) as T;
          }

          // Log error details for debugging
          console.error("LocalStorage error:", {
            type: error.type,
            message: error.message,
            key,
            originalError: error.originalError,
          });
        }
        throw error;
      }
    },
    gcTime,
    retry: (failureCount, error) => {
      // Don't retry on quota exceeded or localStorage not available
      if (error instanceof LocalStorageError) {
        const nonRetryableErrors = [
          LocalStorageErrorType.NOT_AVAILABLE,
          LocalStorageErrorType.QUOTA_EXCEEDED,
          LocalStorageErrorType.SECURITY_ERROR,
        ];
        if (nonRetryableErrors.includes(error.type)) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
};
